import  { FC } from "react";
import { useSelector } from "react-redux";
import TableTab from "../../../components/tabs/table-tab";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { Lookup } from "@/models/Lookup";
import { auditRunReport } from "@/services/auditService";
import { getDoorAccessReport } from "@/services/reportService";
import { getErrorMessage, showError } from "@/services/utilities/ServiceError";
import { ListQuery } from "@/models/ListQuery";
import DoorAccessReportColumns, { DoorAccessReportChildColumns } from "@/models/ColumnDefinitions/DoorAccessReportColumns";
import DoorAccessReportSearch from "@/models/search/DoorAccessReportSearch";
import { DEF_PG_SIZE_REPORTS } from "@/constants/maximum-page-size";
import { Filter } from "@/models/Filter";
import { toast } from "react-toastify";
import { REPORTS_DOOR_ACCESS } from "@/constants/menu-types";
import { getUserFiltersByView } from "@/services/userPreferenceService";
import { FILTER_ACCESS_GROUP, FILTER_ACCESS_TIME_PATTERN, FILTER_COMPANIES, FILTER_DEPARTMENTS, FILTER_DOORS, FILTER_TAGHOLDERS } from "@/constants/filter-types";
import LookupTypes from "@/models/LookupTypes";
import { getCompanyNamesWithPagination } from "@/services/companyService";
import CompanySearch from "@/models/search/CompanySearch";
import { getDepartmentNamesWithPagination } from "@/services/departmentService";
import DepartmentSearch from "@/models/search/DepartmentSearch";
import { getEnrolmentNamesWithPagination } from "@/services/enrolmentService";
import EnrolmentSearch from "@/models/search/EnrolmentSearch";
import { getDoorNamesWithPagination } from "@/services/doorService";
import DoorSearch from "@/models/search/DoorSearch";
import { getSiteNamesWithPagination } from "@/services/siteService";
import SiteSearch from "@/models/search/SiteSearch";
import { getAccessGroupsNamesWithPagination } from "@/services/accessGroupService";
import AccessGroupSearch from "@/models/search/AccessGroupSearch";
import { getAccessTimePatternNamesWithPagination } from "@/services/accessTimePatternService";
import AccessTimePatternSearch from "@/models/search/AccessTimePatternSearch";

interface DoorAccessReportProps {
    reportId: string;
}

const DoorAccessReport: FC<DoorAccessReportProps> = ({ reportId }) => {
    const { translate } = useLocale();
    const userLoginId = useSelector((state: any) => (state.userInfo.id));
    const lookup: Lookup[] = [
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: "masterId",
            values: [],
            dataMethod: getEnrolmentNamesWithPagination,
            searchAccessor: EnrolmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: "locationId",
            values: [],
            dataMethod: getDoorNamesWithPagination,
            searchAccessor: DoorSearch.find(item => item.accessor === "name")?.searchOnName,
            isSingleSelect: true
        },
        {
            name: "accessGroupId",
            values: [],
            dataMethod: (listQuery?: ListQuery) => getAccessGroupsNamesWithPagination(null, listQuery),
            searchAccessor: AccessGroupSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: "accessTimePatternId",
            values: [],
            dataMethod: getAccessTimePatternNamesWithPagination,
            searchAccessor: AccessTimePatternSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        }
    ]

    const getOriginalStaticFilter = async (filterKey: string): Promise<Filter> => {
        switch (filterKey) {
            case "locationId":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.door),
                    options: [],
                    isStatic: true,
                    selected: [{
                        label: translate.byKey(TranslationKeys.required),
                        value: "require_filter",
                        ignore: true
                    }],
                }
            case "companyId":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.company),
                    options: [],
                    isStatic: true,
                    selected: [{
                        label: translate.byKey(TranslationKeys.all),
                        value: "*",
                        ignore: true
                    }],
                }
            case "departmentId":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.department),
                    options: [],
                    isStatic: true,
                    selected: [{
                        label: translate.byKey(TranslationKeys.all),
                        value: "*",
                        ignore: true
                    }],
                }
            case "masterId":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.displayName),
                    options: [],
                    isStatic: true,
                    selected: [{
                        label: translate.byKey(TranslationKeys.all),
                        value: "*",
                        ignore: true
                    }],
                }
            case "accessGroupId":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.accessGroup),
                    options: [],
                    isStatic: true,
                    selected: [{
                        label: translate.byKey(TranslationKeys.all),
                        value: "*",
                        ignore: true
                    }],
                }
            case "accessTimePatternId":
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.accessTimePattern),
                    options: [],
                    isStatic: true,
                    selected: [{
                        label: translate.byKey(TranslationKeys.all),
                        value: "*",
                        ignore: true
                    }],
                }
            default:
                return {
                    isCustomFilter: true,
                    key: filterKey,
                    label: filterKey,
                    options: [],
                    isStatic: true,
                    selected: [{
                        label: translate.byKey(TranslationKeys.all),
                        value: "*",
                        ignore: true
                    }],
                }
        }
    }

    const initialiseUserPreferences = async (setDetails: any) => {
        const staticFilterKeys: string[] = ["locationId", "companyId", "departmentId", "masterId", "accessGroupId", "accessTimePatternId"];
        let customFilter: Filter[] = [];

        try {
            const response = await getUserFiltersByView(userLoginId, REPORTS_DOOR_ACCESS, reportId);

            if (response.find(filter => filter.filterId === FILTER_DOORS) === undefined) {
                customFilter.push(await getOriginalStaticFilter("locationId"));
            }
            if (response.find(filter => filter.filterId === FILTER_COMPANIES) === undefined) {
                customFilter.push(await getOriginalStaticFilter("companyId"));
            }
            if (response.find(filter => filter.filterId === FILTER_DEPARTMENTS) === undefined) {
                customFilter.push(await getOriginalStaticFilter("departmentId"));
            }
            if (response.find(filter => filter.filterId === FILTER_TAGHOLDERS) === undefined) {
                customFilter.push(await getOriginalStaticFilter("masterId"));
            }
            if (response.find(filter => filter.filterId === FILTER_ACCESS_GROUP) === undefined) {
                customFilter.push(await getOriginalStaticFilter("accessGroupId"));
            }
            if (response.find(filter => filter.filterId === FILTER_ACCESS_TIME_PATTERN) === undefined) {
                customFilter.push(await getOriginalStaticFilter("accessTimePatternId"));
            }
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setDetails(userLoginId, REPORTS_DOOR_ACCESS, customFilter, reportId, staticFilterKeys);
        }
    };

    const getDataMethod = async (listParameters?: ListQuery) => {
        try {
            await auditRunReport(reportId);
            return getDoorAccessReport(translate, toast.warn, listParameters);
        } catch (error) {
            return Promise.reject(getErrorMessage(error));
        }
    }

    return <TableContextProvider
        maxPageSize={DEF_PG_SIZE_REPORTS}
        dataMethod={getDataMethod}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        tableDescriptor={DoorAccessReportColumns}
        searchDescriptor={DoorAccessReportSearch}
        useFilter
        useSort
        usePagination
        useSearch
        useTable
        lookup={lookup}
        getOriginalStaticFilter={getOriginalStaticFilter}
        loadDataOnDemand
        runDataOnDemandButtonLabel={translate.byKey(TranslationKeys.runReport)}
        childColumns={DoorAccessReportChildColumns}>
        <TableTab
            title={''}
            reportTitle={translate.byKey(TranslationKeys.doorAccess)}
            selectRows={false}
            cyName="DoorAccessRepoReport"
            useGroupedRows
            highlightExpandedParent
            childTableHeading={translate.byKey(TranslationKeys.accessGroups)} />
    </TableContextProvider >
};

export default DoorAccessReport;
